import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import BusinessCard from "./BusinessCard";
import { SORT_SOONEST } from "./useDiscoverySearch";

/**
 * The result grid: loading skeletons, the four things that can be true instead,
 * and the pager.
 *
 * Shared by the public `/discover` page and the signed-in `Book` tab so the two
 * cannot disagree about what "nothing matched" looks like. The only thing that
 * differs between them is what happens when a slot is chosen, and that arrives
 * as `onSelectSlot`: absent (public) the slots are links to the business's own
 * booking page; present (signed in) they open the booking modal in place.
 */
const DiscoverResults = ({ search, onSelectSlot }) => {
  const { t } = useTranslation();
  const { filters, results, loading, error, totalPages, goToPage } = search;

  if (loading) {
    return (
      <div className="grid gap-5 md:grid-cols-2">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-52 animate-pulse rounded-card border border-ink-200 bg-white"
            aria-hidden="true"
          />
        ))}
        <span className="sr-only">{t("Discover.loading")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <p className="rounded-card border border-red-200 bg-red-50 px-6 py-8 text-center text-fluid-base text-red-700">
        {t("Discover.error")}
      </p>
    );
  }

  if (results.items.length === 0) {
    return (
      <div className="rounded-card border border-dashed border-ink-300 bg-white px-6 py-16 text-center">
        <span className="inline-flex size-12 items-center justify-center rounded-full bg-ink-50 text-ink-400">
          <i className="bx bx-search-alt text-2xl" aria-hidden="true" />
        </span>
        <p className="mt-4 text-fluid-base font-medium text-ink-700">
          {t("Discover.emptyTitle")}
        </p>
        <p className="mt-1 text-fluid-sm text-ink-500">
          {filters.sort === SORT_SOONEST
            ? t("Discover.emptySoonest")
            : filters.date
              ? t("Discover.emptyWithDate")
              : t("Discover.emptyHint")}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* `grid-cols-1`, not the implicit column: an implicit track is sized
          `auto`, so it grew to the card's widest unwrappable line (the
          truncated services row, the title) and pushed the card past a phone's
          edge instead of letting `truncate` do its job. */}
      <ul className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {results.items.map((business) => (
          <li key={business.id} className="flex min-w-0">
            <div className="w-full min-w-0">
              <BusinessCard
                business={business}
                date={filters.date}
                onSelectSlot={onSelectSlot}
              />
            </div>
          </li>
        ))}
      </ul>

      {totalPages > 1 ? (
        <nav
          className="mt-8 flex items-center justify-center gap-3"
          aria-label={t("Discover.pagination")}
        >
          <button
            type="button"
            disabled={filters.page <= 1}
            onClick={() => goToPage(filters.page - 1)}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-600 transition hover:border-ink-300 disabled:pointer-events-none disabled:opacity-40"
          >
            <i className="bi bi-chevron-left text-xs" aria-hidden="true" />
            {t("Discover.previous")}
          </button>
          <span className="text-sm text-ink-500">
            {t("Discover.pageOf", { page: filters.page, total: totalPages })}
          </span>
          <button
            type="button"
            disabled={filters.page >= totalPages}
            onClick={() => goToPage(filters.page + 1)}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-600 transition hover:border-ink-300 disabled:pointer-events-none disabled:opacity-40"
          >
            {t("Discover.next")}
            <i className="bi bi-chevron-right text-xs" aria-hidden="true" />
          </button>
        </nav>
      ) : null}
    </>
  );
};

DiscoverResults.propTypes = {
  /** The whole return value of `useDiscoverySearch`. */
  search: PropTypes.shape({
    filters: PropTypes.object.isRequired,
    results: PropTypes.shape({
      items: PropTypes.array,
      total: PropTypes.number,
    }).isRequired,
    loading: PropTypes.bool,
    error: PropTypes.bool,
    totalPages: PropTypes.number,
    goToPage: PropTypes.func.isRequired,
  }).isRequired,
  /** `(business, slot) => void`. Omit to keep the public link behaviour. */
  onSelectSlot: PropTypes.func,
};

export default DiscoverResults;
